import React, { useCallback, useEffect, useRef, useState } from 'react';
import { authAPI } from '../services/api';
import { endSession } from '../services/session';
import { getLang } from './LanguageToggle';
import './IdleTimeout.css';

const IDLE_LIMIT_MS = 5 * 60 * 1000;
const COUNTDOWN_SECONDS = 60;
const COUNTDOWN_MS = COUNTDOWN_SECONDS * 1000;
const CHECK_INTERVAL_MS = 5 * 1000;
const HEALTH_CHECK_INTERVAL_MS = 5 * 60 * 1000;

const TRANSLATIONS = {
  en: {
    title: 'Are you still there?',
    body: 'You have been inactive for a while. For your security, you will be signed out automatically.',
    countdown: 'Signing out in',
    seconds: 'seconds',
    stay: 'Keep me signed in',
    logout: 'Log out now',
    checking: 'Checking…',
    expired_title: 'Your session has ended',
    expired_body: 'For your security, your PRISM session is no longer active. Sign in again to continue.',
    expired_countdown: 'Returning to sign-in in',
    expired_action: 'Sign in again',
  },
  fr: {
    title: 'Êtes-vous toujours là?',
    body: 'Vous êtes inactif depuis un certain temps. Pour votre sécurité, vous serez déconnecté automatiquement.',
    countdown: 'Déconnexion dans',
    seconds: 'secondes',
    stay: 'Rester connecté',
    logout: 'Se déconnecter maintenant',
    checking: 'Vérification…',
    expired_title: 'Votre session est terminée',
    expired_body: "Pour votre sécurité, votre session PRISM n'est plus active. Connectez-vous de nouveau pour continuer.",
    expired_countdown: 'Retour à la connexion dans',
    expired_action: 'Se reconnecter',
  },
};

const IdleTimeout = () => {
  // null = app is live and usable. 'idle' = the idle limit elapsed with no
  // mouse or keyboard, session still valid, asking whether to keep it.
  // 'expired' = the server already rejected us; the only way forward is a
  // fresh sign-in. Conflating the two used to make "I'm here" a dead button on
  // an expired session — the modal closed and the page looked alive but every
  // request failed.
  const [prompt, setPrompt] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const [checking, setChecking] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const promptRef = useRef(null);
  const deadlineRef = useRef(0);
  const loggingOutRef = useRef(false);
  const countdownTimerRef = useRef(null);
  const checkTimerRef = useRef(null);
  const healthTimerRef = useRef(null);

  const lang = getLang();
  const t = (key) => (TRANSLATIONS[lang] || TRANSLATIONS.en)[key] || key;

  const secondsUntil = (deadline) => Math.max(0, Math.ceil((deadline - Date.now()) / 1000));

  // The countdown runs against an absolute deadline rather than a tally of
  // ticks, so it stays correct when the browser throttles timers in a
  // background tab, or freezes them outright while the laptop is asleep.
  const openPrompt = useCallback((reason) => {
    promptRef.current = reason;
    deadlineRef.current = reason === 'expired'
      ? Date.now() + COUNTDOWN_MS
      : lastActivityRef.current + IDLE_LIMIT_MS + COUNTDOWN_MS;
    setPrompt(reason);
    setSecondsLeft(secondsUntil(deadlineRef.current));
  }, []);

  // Drafts are intentionally preserved across BOTH the manual "Log out now"
  // path and the auto-countdown path — per-user namespacing in
  // services/draftStorage.js keeps each clinician's typed messages
  // isolated, so the Restore banner can re-offer the unsent question on
  // next sign-in regardless of how the user left.
  const performLogout = useCallback(async () => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    // authAPI.logout() already told the other tabs, so don't broadcast twice.
    try { await authAPI.logout(); } catch (_) {}
    endSession({ broadcast: false });
  }, []);

  // The single idle judgement, on wall-clock time. Time spent on another tab
  // or another app is inactivity like any other: the clock does NOT stop while
  // PRISM is hidden, and coming back is not itself activity. An earlier
  // version counted only visible time and reset the clock on return, so a
  // clinician could leave PRISM open behind another tab indefinitely and find
  // the chat page still sitting there — exactly the state this feature exists
  // to prevent.
  //
  // allowPrompt is false whenever nobody was watching the window as the limit
  // passed — a hidden tab, or one being returned to. There is no point running
  // a countdown at a dialog they never saw, so those cases sign out on the
  // spot and the tab they come back to is already the login page. The
  // countdown exists only for the case this feature was asked for: someone in
  // front of the main window or admin panel who stopped touching the mouse and
  // keyboard.
  const evaluateIdle = useCallback(({ allowPrompt }) => {
    if (loggingOutRef.current) return;
    if (promptRef.current === 'expired') return;
    const idleMs = Date.now() - lastActivityRef.current;
    if (idleMs < IDLE_LIMIT_MS) return;
    if (!allowPrompt || idleMs >= IDLE_LIMIT_MS + COUNTDOWN_MS) {
      performLogout();
      return;
    }
    if (!promptRef.current) openPrompt('idle');
  }, [openPrompt, performLogout]);

  // Session check.
  //   silent=false (heartbeat) → tab is visible AND user has been here the
  //     whole time. Show the "session has ended" modal on failure so the
  //     user sees why they're being signed out.
  //   silent=true  (visibility-change) → tab just came back from hidden.
  //     User wasn't watching, so jump straight to /login with no modal flash.
  // _skipAuthRedirect bypasses services/api.js's interceptor — without it,
  // a 401 here fires the interceptor's redirect mid-render and the modal
  // flickers visible for one frame before the page navigates away.
  const verifySession = useCallback(async ({ silent = false } = {}) => {
    if (loggingOutRef.current) return;
    if (promptRef.current) return;
    try {
      await authAPI.getCurrentUser({ _skipAuthRedirect: true });
    } catch (err) {
      if (typeof window === 'undefined') return;
      if (window.location.pathname.endsWith('/login')) return;
      const tabVisible = typeof document !== 'undefined' && !document.hidden;
      if (tabVisible && !silent) {
        openPrompt('expired');
        return;
      }
      loggingOutRef.current = true;
      endSession();
    }
  }, [openPrompt]);

  const recordActivity = useCallback(() => {
    if (promptRef.current || loggingOutRef.current) return;
    lastActivityRef.current = Date.now();
  }, []);

  // "Keep me signed in". Confirms with the server before closing the modal:
  // the whole point of this feature is that a visible PRISM window always
  // means a working one, so we never dismiss on the client's word alone. If
  // the cookie died while the prompt was up (tab backgrounded past the token
  // TTL, admin deactivated the account, backend redeployed), the modal
  // switches to the 'expired' variant instead of handing back a dead app.
  const keepSession = useCallback(async () => {
    if (checking) return;
    setChecking(true);
    // Stop the clock first: a slow /me must not let the countdown hit zero and
    // sign the user out from under a click they already made.
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    try {
      await authAPI.getCurrentUser({ _skipAuthRedirect: true });
      promptRef.current = null;
      setPrompt(null);
      setSecondsLeft(COUNTDOWN_SECONDS);
      lastActivityRef.current = Date.now();
    } catch {
      openPrompt('expired');
    } finally {
      setChecking(false);
    }
  }, [checking, openPrompt]);

  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((ev) => window.addEventListener(ev, recordActivity, { passive: true }));

    // Coming back to the tab is the moment to settle up, not to forgive the
    // time away — so evaluate the idle clock BEFORE any pointer event over the
    // page can look like fresh activity, and only then check the session is
    // still alive. allowPrompt: false because whatever happened while they
    // were away, they should be looking at a login page now, not a timer.
    // verifySession leaves for /login on ANY /me failure (401, network, 5xx),
    // not just the 401 the interceptor would have caught.
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      evaluateIdle({ allowPrompt: false });
      verifySession({ silent: true });
    };
    const onFocus = () => evaluateIdle({ allowPrompt: false });
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, recordActivity));
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
    };
  }, [recordActivity, evaluateIdle, verifySession]);

  // Runs whether or not the tab is visible. Browsers throttle background
  // intervals to roughly once a minute, which is plenty for a limit measured
  // in minutes, and the visibility handler above covers a tab that was frozen
  // entirely — so a hidden tab signs itself out on time and is already showing
  // the login page whenever the clinician gets back to it, instead of waiting
  // for someone to look at it first.
  useEffect(() => {
    checkTimerRef.current = setInterval(
      () => evaluateIdle({ allowPrompt: !document.hidden }),
      CHECK_INTERVAL_MS,
    );
    return () => {
      if (checkTimerRef.current) clearInterval(checkTimerRef.current);
    };
  }, [evaluateIdle]);

  // Heartbeat: while the tab is visible, ping /me every 5 min so an expired
  // cookie is detected before the user clicks anything. Skipped while hidden
  // so a backgrounded tab does not silently keep the server session alive —
  // we want background cookies to die naturally; the visibility-change check
  // above catches them on return.
  useEffect(() => {
    healthTimerRef.current = setInterval(() => {
      if (loggingOutRef.current) return;
      if (promptRef.current) return;
      if (document.hidden) return;
      verifySession();
    }, HEALTH_CHECK_INTERVAL_MS);
    return () => {
      if (healthTimerRef.current) clearInterval(healthTimerRef.current);
    };
  }, [verifySession]);

  useEffect(() => {
    if (!prompt) return undefined;

    const tick = () => {
      const remaining = secondsUntil(deadlineRef.current);
      if (remaining <= 0) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
        // 'expired' means the cookie is already gone, so there is no session
        // left to POST /logout for — just land on the sign-in screen. The
        // modal self-clears either way; it is never a state the user has to
        // dismiss manually.
        if (prompt === 'expired') endSession();
        else performLogout();
        return;
      }
      setSecondsLeft(remaining);
    };

    tick();
    countdownTimerRef.current = setInterval(tick, 250);

    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    };
  }, [prompt, performLogout]);

  if (!prompt) return null;

  const expired = prompt === 'expired';
  const pct = Math.max(0, Math.min(100, (secondsLeft / COUNTDOWN_SECONDS) * 100));

  return (
    <div
      className="idle-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="idle-timeout-title"
    >
      <div className="idle-modal">
        <h2 id="idle-timeout-title" className="idle-modal-title">
          {expired ? t('expired_title') : t('title')}
        </h2>
        <p className="idle-modal-body">{expired ? t('expired_body') : t('body')}</p>

        <div className="idle-countdown" aria-live="polite" aria-atomic="true">
          <span className="idle-countdown-label">
            {expired ? t('expired_countdown') : t('countdown')}
          </span>
          <span className="idle-countdown-value">{secondsLeft}</span>
          <span className="idle-countdown-unit">{t('seconds')}</span>
        </div>

        <div className="idle-countdown-bar" aria-hidden="true">
          <div className="idle-countdown-bar-fill" style={{ width: `${pct}%` }} />
        </div>

        <div className="idle-modal-actions">
          {expired ? (
            <button
              type="button"
              className="idle-modal-btn idle-modal-btn-primary"
              onClick={() => endSession()}
              autoFocus
            >
              {t('expired_action')}
            </button>
          ) : (
            <>
              <button
                type="button"
                className="idle-modal-btn idle-modal-btn-primary"
                onClick={keepSession}
                disabled={checking}
                autoFocus
              >
                {checking ? t('checking') : t('stay')}
              </button>
              <button
                type="button"
                className="idle-modal-btn idle-modal-btn-secondary"
                onClick={() => performLogout()}
                disabled={checking}
              >
                {t('logout')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default IdleTimeout;
